import { useEffect, useRef, useState, useCallback } from 'react';

import mapboxgl from 'mapbox-gl';

import { MAP_CONSTANTS } from './mapConstants';
import { setupTerrain, setupPathLayer, setupMapControls } from './mapSetupUtils';
import { MarkerManager } from './mapTypes';
import { createMarkerManager } from './markerManager';

mapboxgl.accessToken = 'pk.eyJ1IjoianVsaWFub2VzIiwiYSI6ImNrOGNwbXVzbTA2bnMzZW95bmhvemIwbGkifQ.vHzrkhopow_IrtZm5yP5iA';

export function useMapInitialization(
  mapContainer: React.RefObject<HTMLDivElement | null>,
  initialZoom: number,
  customZoomControls: boolean,
  hideControls: boolean
) {
  const map = useRef<mapboxgl.Map | null>(null);
  const markerManagerRef = useRef<MarkerManager>(createMarkerManager());
  const [mapLoaded, setMapLoaded] = useState(false);

  const cleanupMap = useCallback(() => {
    if (map.current) {
      markerManagerRef.current.removeAllMarkers();
      map.current.remove();
      map.current = null;
      setMapLoaded(false);
    }
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      cleanupMap();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [cleanupMap]);

  useEffect(() => {
    const navigationEntries = performance.getEntriesByType('navigation');
    if (navigationEntries.length > 0 && (navigationEntries[0] as PerformanceNavigationTiming).type === 'reload') {
      cleanupMap();
    }
  }, [cleanupMap]);

  useEffect(() => {
    if (!map.current && mapContainer.current) {
      markerManagerRef.current.removeAllMarkers();

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/satellite-v9',
        center: MAP_CONSTANTS.DEFAULT_CENTER,
        zoom: initialZoom,
        attributionControl: false
      });

      setupMapControls(map.current, customZoomControls, hideControls);

      map.current.on('load', () => {
        if (!map.current) return;

        setupTerrain(map.current, map);
        setupPathLayer(map.current);
        setMapLoaded(true);
      });
    }

    return cleanupMap;
  }, [initialZoom, customZoomControls, hideControls, cleanupMap, mapContainer]);

  return { map, markerManagerRef, mapLoaded };
}
